import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as Clipboard from 'expo-clipboard';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { isRestrictedRegion } from '../../utils/restrictedRegion';
import {
  trackShareClicked,
  trackShareCompleted,
  type ShareChannel,
  type ShareTarget,
} from '../../services/shareAnalytics';
import { GamificationShareCard } from './GamificationShareCard';
import { isGamificationShareUrl, type GamificationShareViewModel } from './shareCard';
import { fontWeights } from '../../styles/typographyTokens';

import { accentTextOn } from '../../styles/accentText';
const UI_FONT = Platform.OS === 'web' ? 'var(--ag-font-ui)' : undefined;
const BRAND_FONT = Platform.select({ web: 'var(--ag-font-brand)', ios: 'Georgia', default: 'serif' });

export type GamificationShareLoadStatus = 'loading' | 'ready' | 'unavailable' | 'error';

interface Props {
  visible: boolean;
  onClose: () => void;
  status: GamificationShareLoadStatus;
  model: GamificationShareViewModel | null;
  url: string;
  message: string;
  subject: string;
  onRetry: () => void;
  analytics: ShareTarget;
}

type ShareKind = 'telegram' | 'whatsapp' | 'facebook' | 'email';

export function buildGamificationChannelUrl(
  kind: ShareKind,
  input: { url: string; message: string; subject: string; web: boolean },
): string {
  if (kind === 'telegram') {
    return input.web
      ? `https://t.me/share/url?url=${encodeURIComponent(input.url)}&text=${encodeURIComponent(input.subject)}`
      : `tg://msg_url?url=${encodeURIComponent(input.url)}&text=${encodeURIComponent(input.subject)}`;
  }
  if (kind === 'whatsapp') return `https://wa.me/?text=${encodeURIComponent(input.message)}`;
  if (kind === 'facebook') return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(input.url)}`;
  return `mailto:?subject=${encodeURIComponent(input.subject)}&body=${encodeURIComponent(input.message)}`;
}

/** CORE-709 — dedicated centered share modal for all six gamification types. */
export const GamificationShareModal: React.FC<Props> = ({
  visible,
  onClose,
  status,
  model,
  url,
  message,
  subject,
  onRetry,
  analytics,
}) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  // CORE-966 — the note under the preview promised something the product did not
  // do: the link carried the profile's static tags, so Telegram showed an avatar
  // and not this card. The promise now holds for a `/s/{token}` link, and only
  // for one — an older backend sends no token, and then the honest note is that
  // the recipient sees the ordinary profile preview.
  const richPreview = isGamificationShareUrl(url);
  const previewNote = richPreview
    ? 'Так карточка выглядит и здесь, и в превью ссылки.'
    : 'Так карточка выглядит в Agapella. По ссылке откроется обычное превью профиля.';
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const compact = viewportWidth <= 640;
  const panelWidth = Math.min(620, Math.max(1, viewportWidth - (compact ? 16 : 40)));
  const panelHeight = Math.min(754, Math.max(1, viewportHeight - (compact ? 16 : 32)));
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const panelRef = useRef<any>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const targetRef = useRef(analytics);
  const wasVisible = useRef(false);
  const completed = useRef(false);
  targetRef.current = analytics;

  useEffect(() => {
    if (!visible) {
      setCopied(false);
      setActionError(null);
    }
    if (visible && !wasVisible.current) {
      completed.current = false;
      trackShareClicked(targetRef.current);
    } else if (!visible && wasVisible.current && !completed.current) {
      trackShareCompleted({ ...targetRef.current, outcome: 'dismissed' });
    }
    wasVisible.current = visible;
  }, [visible]);

  // Web accessibility contract: Escape, trapped Tab order and focus restoration.
  useEffect(() => {
    if (!visible || Platform.OS !== 'web' || typeof document === 'undefined') return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusableSelector = 'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';
    const focusFirst = () => {
      const root = panelRef.current as HTMLElement | null;
      const first = root?.querySelector<HTMLElement>(focusableSelector);
      (first ?? root)?.focus?.();
    };
    const timer = window.setTimeout(focusFirst, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        // RN Web parents may receive the matching keyup after this nested modal
        // has unmounted. Swallow that one event so a single Escape closes only
        // the topmost share modal (never its celebration/drawer parent).
        const swallowKeyUp = (keyUpEvent: KeyboardEvent) => {
          if (keyUpEvent.key !== 'Escape') return;
          keyUpEvent.preventDefault();
          keyUpEvent.stopImmediatePropagation();
          document.removeEventListener('keyup', swallowKeyUp, true);
        };
        document.addEventListener('keyup', swallowKeyUp, true);
        window.setTimeout(() => document.removeEventListener('keyup', swallowKeyUp, true), 1000);
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = panelRef.current as HTMLElement | null;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown, true);
      window.setTimeout(() => previousFocusRef.current?.focus?.(), 0);
    };
  }, [visible, onClose]);

  const reportShared = (channel: ShareChannel) => {
    completed.current = true;
    trackShareCompleted({
      ...targetRef.current,
      channel,
      outcome: channel === 'clipboard' ? 'copied' : 'shared',
    });
  };

  const openExternal = async (kind: ShareKind) => {
    setActionError(null);
    const target = buildGamificationChannelUrl(kind, {
      url,
      message,
      subject,
      web: Platform.OS === 'web',
    });
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        if (kind === 'email') {
          window.location.href = target;
        } else {
          const popup = window.open(target, '_blank');
          if (!popup) throw new Error('popup blocked');
          try { popup.opener = null; } catch { /* browser owns the new context */ }
        }
      } else if (await Linking.canOpenURL(target)) {
        await Linking.openURL(target);
      } else {
        // No `url` field — the message already ends with the link, and iOS hands
        // the sheet a second item that «Скопировать» concatenates (CORE-743).
        await Share.share({ message, title: subject });
      }
      reportShared(kind);
      onClose();
    } catch {
      setActionError('Не удалось открыть выбранный сервис. Попробуй ещё раз или скопируй ссылку.');
    }
  };

  const copyLink = async () => {
    setActionError(null);
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        await Clipboard.setStringAsync(url);
      }
      setCopied(true);
      reportShared('clipboard');
    } catch {
      setCopied(false);
      setActionError('Не удалось скопировать ссылку. Попробуй ещё раз.');
    }
  };

  const channels: { key: ShareKind; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
    { key: 'telegram', label: 'Telegram', icon: 'paper-plane-outline' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'logo-whatsapp' },
    ...(isRestrictedRegion(user) ? [] : [{ key: 'facebook' as const, label: 'Facebook', icon: 'logo-facebook' as const }]),
    { key: 'email', label: 'Почта', icon: 'mail-outline' },
  ];

  const ink = isDark ? '#F4EEE8' : '#2C2825';
  const muted = isDark ? '#B8AEA7' : '#746B65';
  const orange = isDark ? '#EF893B' : '#DF6F1D';
  const surface = isDark ? 'rgba(12,17,22,0.986)' : 'rgba(255,253,249,0.985)';
  const surface2 = isDark ? 'rgba(22,27,33,0.94)' : 'rgba(255,249,241,0.94)';
  const stroke = isDark ? 'rgba(235,132,52,0.42)' : 'rgba(217,105,29,0.37)';
  const softStroke = isDark ? 'rgba(255,255,255,0.11)' : 'rgba(86,58,39,0.14)';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable style={styles.overlay} onPress={onClose} accessible={false}>
        <Pressable
          ref={panelRef}
          onPress={(event) => event.stopPropagation()}
          accessibilityViewIsModal
          accessibilityLabel="Поделиться прогрессом Пути Света"
          {...(Platform.OS === 'web' ? ({ role: 'dialog', tabIndex: -1, 'aria-modal': true } as any) : null)}
          style={[
            styles.panel,
            {
              width: panelWidth,
              height: panelHeight,
              borderColor: stroke,
              backgroundColor: surface,
            },
          ]}
        >
          <TouchableOpacity
            testID="gamification-share-close"
            onPress={onClose}
            activeOpacity={0.72}
            accessibilityRole="button"
            accessibilityLabel="Закрыть"
            style={[styles.close, { borderColor: stroke }]}
          >
            <Ionicons name="close" size={25} color={accentTextOn(orange)} />
          </TouchableOpacity>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={[styles.content, compact && styles.contentCompact]}
            showsVerticalScrollIndicator={panelHeight < 754}
            keyboardShouldPersistTaps="handled"
          >
            <Text style={[styles.title, compact && styles.titleCompact, { color: ink }]}>Поделиться</Text>
            <Text style={[styles.subtitle, { color: muted }]}>Отправь свой прогресс друзьям или скопируй ссылку.</Text>

            {status === 'ready' && model ? (
              <>
                <GamificationShareCard model={model} isDark={isDark} testID="gamification-share-card" />
                <Text style={[styles.previewNote, { color: muted }]}>{previewNote}</Text>
              </>
            ) : (
              <View
                style={[
                  styles.stateCard,
                  compact && styles.stateCardCompact,
                  { borderColor: softStroke, backgroundColor: surface2 },
                ]}
              >
                {status === 'loading' ? (
                  <>
                    <ActivityIndicator color={accentTextOn(orange)} />
                    <Text accessibilityLiveRegion="polite" style={[styles.stateTitle, { color: ink }]}>Готовим карточку…</Text>
                    <Text style={[styles.stateCopy, { color: muted }]}>Загружаем актуальные данные Пути Света.</Text>
                  </>
                ) : status === 'unavailable' ? (
                  <>
                    <Ionicons name="lock-closed-outline" size={28} color={accentTextOn(orange)} />
                    <Text accessibilityLiveRegion="polite" style={[styles.stateTitle, { color: ink }]}>Этим пока нельзя поделиться</Text>
                    <Text style={[styles.stateCopy, { color: muted }]}>Сначала получи уровень, достижение или веху — после этого карточка станет доступна.</Text>
                  </>
                ) : (
                  <>
                    <Ionicons name="cloud-offline-outline" size={30} color={accentTextOn(orange)} />
                    <Text accessibilityLiveRegion="assertive" style={[styles.stateTitle, { color: ink }]}>Не удалось загрузить карточку</Text>
                    <Text style={[styles.stateCopy, { color: muted }]}>Проверь соединение и попробуй ещё раз.</Text>
                    <TouchableOpacity onPress={onRetry} accessibilityRole="button" accessibilityLabel="Повторить загрузку" style={[styles.retry, { borderColor: stroke }]}>
                      <Text style={[styles.retryText, { color: accentTextOn(orange) }]}>Повторить</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}

            {status === 'ready' && model ? (
              <>
                <Text style={[styles.sectionLabel, { color: muted }]}>ПОДЕЛИТЬСЯ ЧЕРЕЗ</Text>
                <View style={styles.channels}>
                  {channels.map((channel) => (
                    <TouchableOpacity
                      key={channel.key}
                      onPress={() => openExternal(channel.key)}
                      activeOpacity={0.76}
                      accessibilityRole="button"
                      accessibilityLabel={`Поделиться через ${channel.label}`}
                      style={[
                        styles.channel,
                        { borderColor: softStroke, backgroundColor: surface2 },
                        compact ? styles.channelCompact : styles.channelDesktop,
                      ]}
                    >
                      <Ionicons name={channel.icon} size={17} color={accentTextOn(orange)} />
                      <Text style={[styles.channelLabel, { color: ink }]}>{channel.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <View style={[styles.copyRow, { borderColor: softStroke, backgroundColor: surface2 }]}>
                  <Text numberOfLines={1} style={[styles.link, { color: muted }]}>{url}</Text>
                  <TouchableOpacity
                    onPress={copyLink}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    accessibilityLabel={copied ? 'Ссылка скопирована' : 'Копировать ссылку'}
                    style={[styles.copyButton, { backgroundColor: orange }]}
                  >
                    <Text style={[styles.copyButtonText, { color: isDark ? '#15100C' : '#FFFFFF' }]}>{copied ? 'Скопировано' : 'Копировать'}</Text>
                  </TouchableOpacity>
                </View>
                {actionError ? (
                  <Text accessibilityLiveRegion="assertive" style={styles.actionError}>{actionError}</Text>
                ) : copied ? (
                  <Text accessibilityLiveRegion="polite" style={[styles.success, { color: isDark ? '#A0C864' : '#618F35' }]}>Ссылка скопирована</Text>
                ) : null}
              </>
            ) : null}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    backgroundColor: 'rgba(0,0,0,0.58)',
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(5px)' } as any : null),
  },
  panel: {
    maxWidth: 620,
    maxHeight: 754,
    overflow: 'hidden',
    borderWidth: 1,
    borderRadius: 27,
    ...(Platform.OS === 'web' ? { boxShadow: '0 30px 76px rgba(0,0,0,0.36)' } as any : null),
  },
  close: {
    position: 'absolute',
    zIndex: 4,
    top: 22,
    right: 22,
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 18,
  },
  scroll: { flex: 1 },
  content: { flexGrow: 1, paddingTop: 30, paddingHorizontal: 40, paddingBottom: 32 },
  contentCompact: { paddingTop: 24, paddingHorizontal: 16, paddingBottom: 22 },
  title: { marginRight: 54, fontFamily: BRAND_FONT, fontSize: 31, lineHeight: 40, fontWeight: '500', letterSpacing: -0.47 },
  titleCompact: { fontSize: 27, lineHeight: 35 },
  subtitle: { marginTop: 0, marginRight: 54, marginBottom: 24, fontFamily: UI_FONT, fontSize: 14, lineHeight: 20 },
  previewNote: { marginTop: 10, marginBottom: 19, fontFamily: UI_FONT, fontSize: 12, lineHeight: 17, textAlign: 'center' },
  sectionLabel: { marginBottom: 9, fontFamily: UI_FONT, fontSize: 11, lineHeight: 15, fontWeight: fontWeights.bold, letterSpacing: 0.99 },
  channels: { flexDirection: 'row', flexWrap: 'wrap', gap: 9 },
  channel: { height: 58, alignItems: 'center', justifyContent: 'center', gap: 3, borderWidth: 1, borderRadius: 14 },
  channelDesktop: { flexBasis: 110, flexGrow: 1 },
  channelCompact: { flexBasis: '47%', flexGrow: 1 },
  channelLabel: { fontFamily: UI_FONT, fontSize: 11, lineHeight: 14, fontWeight: fontWeights.bold },
  copyRow: { height: 50, marginTop: 14, flexDirection: 'row', alignItems: 'center', overflow: 'hidden', borderWidth: 1, borderRadius: 14 },
  link: { flex: 1, minWidth: 0, paddingHorizontal: 15, fontFamily: UI_FONT, fontSize: 12, lineHeight: 16 },
  copyButton: { height: 36, marginRight: 7, paddingHorizontal: 15, alignItems: 'center', justifyContent: 'center', borderRadius: 10 },
  copyButtonText: { fontFamily: UI_FONT, fontSize: 12, lineHeight: 16, fontWeight: fontWeights.bold },
  stateCard: { aspectRatio: 1200 / 630, alignItems: 'center', justifyContent: 'center', padding: 18, borderWidth: 1, borderRadius: 20 },
  stateCardCompact: { aspectRatio: undefined, minHeight: 220 },
  stateTitle: { marginTop: 10, fontFamily: UI_FONT, fontSize: 17, lineHeight: 23, fontWeight: fontWeights.bold, textAlign: 'center' },
  stateCopy: { maxWidth: 360, marginTop: 4, fontFamily: UI_FONT, fontSize: 13, lineHeight: 19, textAlign: 'center' },
  retry: { marginTop: 12, paddingVertical: 8, paddingHorizontal: 15, borderWidth: 1, borderRadius: 12 },
  retryText: { fontFamily: UI_FONT, fontSize: 13, lineHeight: 18, fontWeight: fontWeights.bold },
  actionError: { marginTop: 9, color: '#C9473A', fontFamily: UI_FONT, fontSize: 12, lineHeight: 17, textAlign: 'center' },
  success: { marginTop: 9, fontFamily: UI_FONT, fontSize: 12, lineHeight: 17, fontWeight: fontWeights.bold, textAlign: 'center' },
});

export default GamificationShareModal;
